import type { PropAsset } from './propValues.types';
import type { GalleryItemData } from './components/GalleryItem/GalleryItem';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import { useCallback, useMemo } from 'react';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';

// If gallery autofill is on, the thumbnail rendering is slightly more expensive than just an image.
// 50 seems to be a balance between performance and too small of a page.
const PAGE_SIZE = 50;
const PAGE_QUERY_PARAM = 'page';

const isValidValueProp = (prop: PropAsset, isMobile: boolean) => {
  const hasMobileContent = prop.contentList.some(c => c.responsiveTarget === 'mobile');
  const hasDesktopContent = prop.contentList.some(c => c.responsiveTarget === 'desktop');

  if (isMobile && !hasMobileContent) {
    return false;
  }

  if (!isMobile && !hasDesktopContent) {
    return false;
  }

  return true;
};

const isValuePropFits = (prop: PropAsset, gridRows: number) => {
  return prop.row < gridRows;
};

const calculateSpaceOccupied = (items: GalleryItemData[]) => {
  return items.reduce((acc, item) => acc + (('columns' in item && item.columns) || 1), 0);
};

export const useStationeryPagination = (templatesWithProps: ReadonlyArray<GalleryItemData>, columnCount: number, isSearchActive: boolean, pageSize: number = PAGE_SIZE) => {
  const isMobile = useIsMobileScreen();
  const [pageQueryParam, setPage] = useManagedSearchParam(PAGE_QUERY_PARAM);
  const currentPage = parseInt(pageQueryParam || '1');

  const { items, pages, effectivePage }: { items: GalleryItemData[]; pages: number; effectivePage: number } = useMemo(() => {
    // When search is active, value props are injected upstream by search in a specific order, and we just paginate the flat list.
    if (isSearchActive) {
      const pages = Math.ceil(templatesWithProps.length / pageSize);
      const effectivePage = Math.min(currentPage, pages || 1);
      const offset = (effectivePage - 1) * pageSize;
      return {
        items: templatesWithProps.slice(offset, offset + pageSize) as GalleryItemData[],
        pages,
        effectivePage,
      };
    }

    const galleryGridRows = pageSize / columnCount - 1;

    const valueProps = templatesWithProps.filter(template => template.type === 'prop') as (PropAsset & { type: 'prop' })[];
    const validValueProps = valueProps.filter(prop => isValidValueProp(prop, isMobile) && isValuePropFits(prop, galleryGridRows));
    const spaceOccupiedForValueProps = calculateSpaceOccupied(validValueProps);

    const templatesWithoutValueProps = templatesWithProps.filter(template => template.type !== 'prop');
    const totalItems = spaceOccupiedForValueProps + templatesWithoutValueProps.length;

    const pages = Math.ceil(totalItems / pageSize);
    const effectivePage = Math.min(currentPage, pages || 1);

    // If we're on the first page, we show value props
    const valuePropsToDisplay = effectivePage > 1 ? [] : validValueProps;

    const templatesOffset = effectivePage === 1 ? 0 : pageSize * (effectivePage - 1) - spaceOccupiedForValueProps;
    const templatesLimit = effectivePage === 1 ? pageSize - spaceOccupiedForValueProps : pageSize;

    const templatesToDisplay = templatesWithoutValueProps.slice(templatesOffset, templatesOffset + templatesLimit);
    const items = [...templatesToDisplay, ...valuePropsToDisplay];

    return { items, pages, effectivePage };
  }, [templatesWithProps, currentPage, isMobile, columnCount, isSearchActive, pageSize]);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page.toString());
    },
    [setPage],
  );

  return { items, page: effectivePage, pages, setPage: handlePageChange };
};
